import { publishEnvelope, subscribeEnvelope } from '@/shared/realtime/bus';
import { fetchNotices } from './api';

/**
 * 补弹（catch-up）——**断线期间漏掉的**重要通知，重连后补一次提示。
 *
 * ## 为什么需要它
 *
 * 铃铛帧（`notification.created`）走 Redis pub/sub ⇒ **离线用户收不到**
 * （Redis 直接把消息丢掉；服务端却以为投出去了——`delivered_at` 记的是
 * 「投给了 Redis」，不是「用户收到了」）⇒ **断线期间发生的 IMPORTANT 通知，
 * 那次 toast 就永久丢了**。
 *
 * ⇒ 这里补一次：**重连后**按**未读行**把漏掉的 IMPORTANT 通知弹一遍。
 *
 * ## 为什么不用「续传」（`Last-Event-ID` + 服务端重放）
 *
 * - 续传要的是「一帧都不漏」⇒ 得在服务端存一份**可重放的事件日志**
 *   （Redis Streams + 每用户游标）；而**行**已经是那份日志了（耐久、
 *   可审计、按用户查）⇒ 再存一份就是**两份账本**，不一致时不知道信谁；
 * - `Last-Event-ID` 是**连接级、单值、被覆盖**的 ⇒ 多标签页要么各弹一遍，
 *   要么共享游标（一个标签页推进了，另一个漏收）；
 * - 本平台**帧 = 提示（允许丢）** ⇒ 丢的只是「打断」，不是数据
 *   （重连后前端本来就会全量失效重拉）⇒ 值得补的是「别让人错过」，
 *   不是「一帧不差」。
 *
 * ## 判据：哪些行算「漏掉的」
 *
 * **水位** = 「我已经处理到哪一帧了」= 收到过的最大帧 `ts`（**服务端时钟**，
 * 与行的 `created_at` 同一把尺子）。行比它新 ⇒ 那条帧要么没发、要么没收到
 * ⇒ 漏了。
 *
 * ⚠️ 水位存 **sessionStorage**：跨刷新保留（**刷新 ≠ 重连** ⇒ 刷新后不该
 * 补弹一遍旧的）；换标签页各算各的（各标签页本来就各连各的流）。
 */

/** 一次补弹最多弹几条——离线久了可能积压很多，全弹就是刷屏。 */
const MAX_CATCH_UP_TOASTS = 3;

/** 补弹时拉多少行未读——只取最新的这一页（后端按 `created_at` **倒序**）。 */
const CATCH_UP_FETCH_SIZE = 50;

/** 水位的存储键（sessionStorage）。 */
const WATERMARK_KEY = 'notifications:lastFrameTs';

/**
 * 读水位（毫秒）——读不到就是 0。
 *
 * ⚠️ **0 的含义是「没有基准」**：从没连上过 ⇒ 没有「断线期间」可言
 * ⇒ 不补弹（否则每次开页面都把历史 IMPORTANT 弹一遍）。
 */
function readWatermark(): number {
  try {
    const raw = sessionStorage.getItem(WATERMARK_KEY);
    if (raw === null) return 0;
    const value = Number(raw);
    return Number.isFinite(value) ? value : 0;
  } catch {
    // 存储不可用（隐私模式 / 禁用 cookie）⇒ 当作没有基准。
    return 0;
  }
}

/** 写水位——**只前进不后退**（时钟回拨 / 乱序帧都不能让水位倒退）。 */
function writeWatermark(ts: number): void {
  try {
    sessionStorage.setItem(WATERMARK_KEY, String(ts));
  } catch {
    // 存不进去只是丢跨刷新的水位，不影响本次会话内的补弹。
  }
}

/** 水位推进——比当前水位新才动。 */
function advanceWatermark(ts: number): void {
  if (!Number.isFinite(ts) || ts <= 0) return;
  if (ts <= readWatermark()) return;
  writeWatermark(ts);
}

/**
 * 订阅事件总线，跟踪「我处理到哪一帧了」。
 *
 * ⚠️ **订阅的是总线而不是 SSE**：总线上有业务帧也有铃铛帧 ⇒ 水位推进到
 * 「最近见过的任何一帧」⇒ 比「最近见过的铃铛帧」更保守（宁可多补也不漏）。
 *
 * @returns 退订函数。
 */
export function watchFrameWatermark(): () => void {
  return subscribeEnvelope((envelope) => {
    const ts = typeof envelope.ts === 'string' ? Date.parse(envelope.ts) : Number.NaN;
    advanceWatermark(ts);
  });
}

/**
 * 补弹——把**漏掉的** IMPORTANT 通知逐条弹出来。返回弹了几条。
 *
 * ⚠️ **只弹 IMPORTANT**：NORMAL 的行只进列表（打断强度是行上的分档，
 * 前端不重判——与实时路径同口径）。
 *
 * ⚠️ **按创建时刻从旧到新**：等得最久的那几条最可能还没处理
 * ⇒ 先弹它们。剩下的在铃铛里（角标显示未读数）。
 */
export async function catchUpMissedToasts(): Promise<number> {
  const watermark = readWatermark();
  if (watermark <= 0) return 0;
  const page = await fetchNotices({
    page: 1,
    size: CATCH_UP_FETCH_SIZE,
    unreadOnly: true,
  });
  const missed = page.items
    .filter((row) => row.importance === 'IMPORTANT')
    .filter((row) => {
      const created = Date.parse(row.created_at);
      return Number.isFinite(created) && created > watermark;
    })
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
    .slice(0, MAX_CATCH_UP_TOASTS);
  for (const row of missed) {
    // 公告行**走公告帧规则**重放（正文各一条、各弹一条）——与实时路径同口径
    // （实时 IMPORTANT 公告弹「系统公告」，断线漏掉的也该弹）。
    if (row.frame === 'notification.broadcast') {
      if (row.message !== null && row.message !== undefined) {
        publishEnvelope({
          type: 'notification.broadcast',
          ts: row.created_at,
          importance: row.importance,
          message: row.message,
          // 行 id —— 合成信封**自报家门**（可追溯、可去重）。
          notification_id: row.id,
        });
      }
      continue;
    }
    // 合成一条**铃铛帧信封** ⇒ 走**同一条** toast 链（`shared/realtime/bus`
    // ⇒ `SseToasts`）⇒ 补弹与实时弹**长得一模一样**，不另写一套渲染。
    //
    // ⚠️ **事实槽位照抄行里的那几列**（`order_no` / `stage` / `action` /
    // `code`）：它们与铃铛帧的同名槽位**同源**（后端一个抽取点、两个载体）⇒
    // 补弹这条的**标题与分档**与实时那条一致（不然用户会看到「同一条通知，
    // 重连补弹时说的是另一件事」）。
    //
    // ⚠️ **协议细节（原始 Steam 码 / 失败步骤）补弹拿不到**——行读面历来不给
    // 载荷快照 ⇒ 那两行只在实时浮层里有（既有缺口，非本次引入）。
    //
    // ⚠️ `ts` 照抄行 `created_at` ⇒ 浮层时间行把补弹的旧事与实时的新事
    // 分得开（同一条链里 `SseToasts` 读信封 `ts` 印相对时间）。
    //
    // ⚠️ 主体账号名取**行的 `username` 列**（落行时 `username=subject`）⇒
    // 补弹也有主角（「alice」药丸），与实时那条的 `subject` 槽位同源。
    publishEnvelope({
      type: 'notification.created',
      ts: row.created_at,
      importance: row.importance,
      frame: row.frame,
      subject: row.username,
      cdkey: row.cdkey,
      order_no: row.order_no,
      stage: row.stage,
      action: row.action,
      code: row.code,
      // 行 id —— 合成信封**自报家门**（可追溯、可去重）。
      // ⚠️ 实时铃铛帧**没有**这个键（帧载荷不带行 id）⇒ 这里补上只是多一条信息，
      // 不改变消费侧的行为（`notices.ts` 只读 ts/importance/frame/事实槽位）。
      notification_id: row.id,
    });
  }
  if (missed.length > 0) {
    // 补过的也算「处理到了」⇒ 水位推到最后一条的创建时刻
    // （否则下次重连会再弹一遍）。
    const newest = Math.max(...missed.map((row) => Date.parse(row.created_at)));
    advanceWatermark(newest);
  }
  return missed.length;
}
